import React, { useCallback, useState } from 'react';
import { View, Pressable, Platform, FlatList } from 'react-native';
import { Image } from 'expo-image';
import { XStack, YStack, Text } from 'tamagui';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import apis, { connectPlatformCallback } from '@api/api';
import { ADD_INTEGRATION, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import { useAuthRequest, makeRedirectUri, ResponseType } from 'expo-auth-session';
import { createURL } from 'expo-linking';
import { useQueryClient } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface SuggestedIntegrationCardProps {
  integration: {
    _id?: string;
    title: string;
    meta: string;
    description?: string;
    logo?: string;
    endPoint?: string;
    clientId?: string;
    clientSecret?: string;
    clientKey?: string;
    scopes?: string[];
    auth?: any;
    relevance_score?: number;
    relevance_reasoning?: string;
  };
  theme: boolean;
  onConnectionSuccess?: () => void;
}

const SuggestedIntegrationCard: React.FC<SuggestedIntegrationCardProps> = ({
  integration,
  theme,
  onConnectionSuccess
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { authentications } = selectUser();
  const [connecting, setConnecting] = useState(false);

  // Check connection status
  const isHuggingFace = integration.meta?.toLowerCase().replace('-', '') === 'huggingface';
  const isConnected = isHuggingFace
    ? authentications?.huggingface?.token || authentications?.huggingface?.username
    : authentications?.[integration.title.toLowerCase()];

  // Image sources
  const sources = [];
  if (integration.meta) {
    const slug = integration.meta.toLowerCase().replace(/ /g, '');
    sources.push({ uri: `https://cdn.simpleicons.org/${slug}` });
  }
  if (integration.logo) {
    sources.push({ uri: integration.logo });
  }

  // OAuth setup
  const discovery = integration.endPoint ? { authorizationEndpoint: integration.endPoint } : undefined;
  const [request, response, promptAsync] = useAuthRequest(
    {
      clientId: integration.clientId || '',
      scopes: integration.scopes || [],
      ...(integration.meta !== 'youtube' && { clientSecret: integration.clientSecret }),
      redirectUri: Platform.select({
        web: createURL('/universal/integrations'),
        default: `https://api.smartsaas.pro/app-redirect`
      }),
      responseType: ResponseType.Code,
      extraParams: {
        client_key: integration.clientKey || integration.clientId || '',
      }
    },
    discovery
  );

  // Handle OAuth response
  React.useEffect(() => {
    if (response?.type === 'success') {
      const { code } = response.params;
      handleGotCode(code, request.codeVerifier);
    }
  }, [response]);

  const handleGotCode = useCallback(async (code: string, verifier?: string) => {
    try {
      setConnecting(true);
      dispatch({ type: SHOW_LOADING_MODAL });
      const res = await connectPlatformCallback(code, integration.meta, verifier || request.codeVerifier);
      dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data } });
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `${integration.title} connected successfully!`, type: 'success' } });
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      queryClient.refetchQueries({ queryKey: ['integrationData'] });
      onConnectionSuccess?.();
    } catch (error: any) {
      console.error('Integration connection error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to connect ${integration.title}: ${error.message}`, type: 'error' } });
    } finally {
      setConnecting(false);
    }
  }, [integration, dispatch, queryClient, onConnectionSuccess, request]);

  const handleConnect = useCallback(async () => {
    if (isConnected) return;

    // Handle special cases
    if (integration.meta === 'whatsapp' || integration.meta === 'facebook' || integration.meta === 'instagram') {
      // These use special flows - for now, just show a message
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Please connect ${integration.title} from the Integrations screen`, type: 'info' } });
      return;
    }

    if (integration.meta === 'wordpress') {
      // WordPress uses API token - show form instead
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Please connect ${integration.title} from the Integrations screen`, type: 'info' } });
      return;
    }

    if (!integration.endPoint || !integration.clientId) {
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `${integration.title} connection is not yet available`, type: 'warning' } });
      return;
    }

    try {
      await promptAsync({ windowFeatures: { dependant: true, name: 'auth' } });
    } catch (error: any) {
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to start connection: ${error.message}`, type: 'error' } });
    }
  }, [integration, isConnected, promptAsync, dispatch]);

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    border: theme ? '#e5e7eb' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    accent: '#3b82f6',
    success: '#10b981',
  };

  return (
    <View
      style={{
        width: Platform.OS === 'web' ? 280 : 240,
        backgroundColor: cardColors.background,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: cardColors.border,
        padding: 16,
        marginRight: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }}
    >
      <XStack alignItems="center" gap={12} marginBottom={12}>
        {sources.length > 0 && (
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              backgroundColor: theme ? '#f3f4f6' : '#4b5563',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Image
              source={sources}
              contentFit="contain"
              style={{ width: 32, height: 32 }}
            />
          </View>
        )}
        <YStack flex={1}>
          <Text
            fontSize={16}
            fontWeight="600"
            color={cardColors.text}
            numberOfLines={1}
          >
            {integration.title}
          </Text>
          {isConnected && (
            <XStack alignItems="center" gap={4} marginTop={4}>
              <MaterialCommunityIcons
                name="check-circle"
                size={14}
                color={cardColors.success}
              />
              <Text fontSize={12} color={cardColors.success}>
                Connected
              </Text>
            </XStack>
          )}
        </YStack>
      </XStack>

      {integration.description && (
        <Text
          fontSize={12}
          color={cardColors.textSecondary}
          numberOfLines={2}
          marginBottom={12}
        >
          {integration.description}
        </Text>
      )}

      {integration.relevance_reasoning && (
        <View
          style={{
            backgroundColor: theme ? '#f0f9ff' : '#1e3a5f',
            padding: 8,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          <Text fontSize={11} color={theme ? '#0369a1' : '#93c5fd'}>
            💡 {integration.relevance_reasoning}
          </Text>
        </View>
      )}

      {!isConnected && (
        <Pressable
          onPress={handleConnect}
          disabled={connecting}
          style={{
            backgroundColor: connecting ? cardColors.textSecondary : cardColors.accent,
            paddingVertical: 10,
            paddingHorizontal: 16,
            borderRadius: 8,
            alignItems: 'center',
            opacity: connecting ? 0.6 : 1,
          }}
        >
          <Text fontSize={14} fontWeight="600" color="white">
            {connecting ? 'Connecting...' : 'Connect'}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

interface SuggestedIntegrationsListProps {
  integrations: SuggestedIntegrationCardProps['integration'][];
  theme: boolean;
}

export const SuggestedIntegrationsList: React.FC<SuggestedIntegrationsListProps> = ({
  integrations,
  theme,
}) => {
  const queryClient = useQueryClient();

  const handleConnectionSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['integrationData'] });
  }, [queryClient]);

  return (
    <FlatList
      data={integrations}
      horizontal
      showsHorizontalScrollIndicator={false}
      keyExtractor={(item, index) => item._id || item.meta || `integration-${index}`}
      renderItem={({ item }) => (
        <SuggestedIntegrationCard
          integration={item}
          theme={theme}
          onConnectionSuccess={handleConnectionSuccess}
        />
      )}
      contentContainerStyle={{
        paddingHorizontal: 4,
        paddingVertical: 0,
        paddingBottom: 4,
      }}
    />
  );
};

export default SuggestedIntegrationCard;

